import React from "react";
import axios from "axios";
import { loaderRoot } from "src";
// @mui
import LinearProgress from "@mui/material/LinearProgress";
// config
import { HOST_API } from "src/config-global";
// theme
import ThemeProvider from "src/theme";
// redux
import ReduxProvider from "src/redux/redux-provider";
// components
import { SettingsProvider } from "src/components/settings";
// utils
import useSWROrigin from "swr";
import { paths } from "src/routes/paths";
import enqueueSnackbar from "./notify";
import history from "./history";
import i18n from "../locales/i18n";
import { refreshToken } from "./token";
import { errorCodeToCamelCase } from "./error";

// redux
import { dispatch } from "../redux/store";
import { setAuthenticated } from "../redux/slices/userProvider";

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

export const STATUS_CODES = {
  badRequest: 400,
  unauthorized: 401,
  serverError: 500,
};

axiosInstance.interceptors.request.use(
  async config => {
    if (JSON.parse(window.localStorage.getItem("hasLoginProblem") ?? "false")) {
      throw new axios.Cancel();
    }

    const responseResult = await refreshToken(true, false);

    if (responseResult.result === "expiredToken") {
      dispatch(setAuthenticated({ authenticated: false, user: null }));

      // token.js already showed the specific reason when the backend gave one.
      if (!responseResult.errorCode) {
        enqueueSnackbar(i18n.t("error:sessionExpired"), { variant: "warning" });
      }

      setTimeout(() => history.push(paths.auth.login), 2000);

      throw new axios.Cancel();
    }

    if (responseResult.result === "error" || responseResult.result === "notAuthenticated") {
      throw new axios.Cancel();
    }

    config.headers["X-Consuman-TimeZone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (config.showLoader) {
      loaderRoot.render(
        <ReduxProvider>
          <SettingsProvider>
            <ThemeProvider>
              <LinearProgress
                sx={{
                  top: 0,
                  right: 0,
                  left: 0,
                  zIndex: 99999,
                  position: "fixed",
                }}
              />
            </ThemeProvider>
          </SettingsProvider>
        </ReduxProvider>
      );
    }

    return config;
  },
  error => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  response => {
    loaderRoot.render();

    return response.request.responseType !== "blob" ? response.data : response;
  },
  error => {
    loaderRoot.render();

    const status = error?.response?.status;

    switch (status) {
      case STATUS_CODES.badRequest: {
        // Not guaranteed to be ProblemDetails: a proxy error page arrives as a plain string.
        const data = error.response.data ?? {};
        const message = errorCodeToCamelCase(data.type);
        const { detail } = data;

        enqueueSnackbar(i18n.t(`error:${message}`, detail ? { message: detail } : {}), {
          variant: "warning",
        });

        return Promise.reject(error.response.data);
      }
      case STATUS_CODES.unauthorized:
        dispatch(setAuthenticated({ authenticated: false, user: null }));
        enqueueSnackbar(i18n.t("error:sessionExpired"), { variant: "warning" });

        setTimeout(() => history.push(paths.auth.login), 2000);
        return Promise.reject(error.response?.data ?? error);
      case STATUS_CODES.serverError: {
        const message = (
          <div>
            {i18n.t("error:serverError")}

            {error.response.data.detail ? (
              <>
                <br /> {error.response.data.detail}
              </>
            ) : null}
          </div>
        );

        enqueueSnackbar(message, { variant: "error" });

        return Promise.reject(error.response.data);
      }
      default:
        return Promise.reject(error);
    }
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async args => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res;
};

export function useSWR(url) {
  const random = React.useRef(Date.now());
  return useSWROrigin(url ? [url, random] : null, fetcher);
}

// ----------------------------------------------------------------------

export const endpoints = {
  chat: "/api/chat",
  kanban: "/api/kanban",
  calendar: "/api/calendar",
  auth: {
    me: "/api/auth/me",
    login: "/api/auth/login",
    register: "/api/auth/register",
  },
  mail: {
    list: "/api/mail/list",
    details: "/api/mail/details",
    labels: "/api/mail/labels",
  },
  post: {
    list: "/api/post/list",
    details: "/api/post/details",
    latest: "/api/post/latest",
    search: "/api/post/search",
  },
  product: {
    list: "/api/product/list",
    details: "/api/product/details",
    search: "/api/product/search",
  },
};
